import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Breadcrumb } from 'src/app/models/breadcrumb-models/breadcrumb-model/breadcrumb.model';
import { BRoute } from 'src/app/models/breadcrumb-models/breadcrumb-route-model/breadcrumb-route.model';
import { HeaderBreadcrumbService } from 'src/app/components/header-components/header-breadcrumb/header-breadcrumb.service';
import { SnackBarService } from 'src/app/components/shared-components/snack-bar/snack-bar.service';
import { PeriodicReportConfigRequest, PeriodicReportThresholds } from 'src/app/models/turns-models/periodic-report.model';
import { PERIODIC_REPORT_LANGUAGES } from 'src/app/models/turns-models/periodic-report-languages';
import { Desk } from 'src/app/models/turns-models/desk.model';
import { CenterLite } from 'src/app/models/center-model/center-lite.model';
import { DualListBoxItem } from 'src/app/components/shared-components/dual-list-box/dual-list-box.component';
import { DesksTurnsService } from 'src/app/services/turns-services/desks-turns-service/desks-turns.service';
import { PeriodicReportsTurnsService } from 'src/app/services/turns-services/periodic-reports-turns-service/periodic-reports-turns.service';
import { UserStorageService } from 'src/app/services/user-storage-service/user-storage.service';

@Component({
  selector: 'app-turns-reports-periodic-config-dashboard',
  templateUrl: './turns-reports-periodic-config-dashboard.component.html',
  styleUrls: ['./turns-reports-periodic-config-dashboard.component.scss']
})
export class TurnsReportsPeriodicConfigDashboardComponent implements OnInit {
  internalId: string;
  name = '';
  periodicity: 'WEEKLY' | 'MONTHLY' = 'WEEKLY';
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  lang = '';
  readonly languages = PERIODIC_REPORT_LANGUAGES.slice();
  active = true;
  thresholds: PeriodicReportThresholds = { waitThresholdSeconds: 600, goodPercent: 90, warningPercent: 75 };
  recipients: string[] = [];
  recipientInput = '';
  centers: CenterLite[] = [];
  selectedCenters: CenterLite[] = [];
  desks: Desk[] = [];
  availableDesks: Desk[] = [];
  selectedDesks: Desk[] = [];
  ready = false;
  loadFailed = false;
  saving = false;
  errorKey = '';

  constructor(private service: PeriodicReportsTurnsService,
              private desksService: DesksTurnsService,
              private storage: UserStorageService,
              private breadcrumb: HeaderBreadcrumbService,
              private route: ActivatedRoute,
              private router: Router,
              private snack: SnackBarService,
              private translate: TranslateService) { }

  ngOnInit() {
    this.internalId = this.route.snapshot.paramMap.get('id');
    this.lang = this.storage.getLang() || 'en_GB';
    const label = this.internalId ? 'turns-periodic.edit configuration' : 'turns-periodic.new configuration';
    this.breadcrumb.setRoutes(new Breadcrumb('fas fa-cog', label, '/turns/reports/periodic/configs', [
      new BRoute('turns-page.turns', '/turns'),
      new BRoute('turns-page.turns reports', '/turns/reports'),
      new BRoute('turns-periodic.title', '/turns/reports/periodic'),
      new BRoute('turns-periodic.configurations', '/turns/reports/periodic/configs'),
      new BRoute(label)
    ]));
    this.centers = this.storage.getCenterList() || [];
    this.desksService.getDesks().subscribe(
      desks => { this.desks = desks || []; this.loadDetail(); },
      error => { this.loadFailed = true; this.showError(error); }
    );
  }

  private loadDetail() {
    if (!this.internalId) { this.updateAvailableDesks(); this.ready = true; return; }
    this.service.detail(this.internalId).subscribe(
      config => {
        this.name = config.name;
        this.periodicity = config.periodicity;
        this.timeZone = config.timeZone;
        if (config.lang) {
          this.lang = config.lang;
          if (!this.languages.some(language => language.locale === config.lang)) {
            this.languages.push({ locale: config.lang, name: config.lang });
          }
        }
        this.active = config.active;
        this.recipients = config.recipients || [];
        this.selectedCenters = this.centers.filter(center =>
          (config.centers || []).some(selected => selected.internalId === center.InternalId));
        this.selectedDesks = this.desks.filter(desk =>
          (config.turnDesks || []).some(selected => selected.internalId === desk.InternalId));
        this.updateAvailableDesks();
        if (config.thresholdConfig) {
          try {
            this.thresholds = JSON.parse(config.thresholdConfig);
          } catch (error) {
            this.errorKey = 'turns-periodic.invalid thresholds';
            return;
          }
        }
        this.ready = true;
      },
      error => { this.loadFailed = true; this.showError(error); }
    );
  }

  private updateAvailableDesks() {
    const centerIds = this.selectedCenters.map(center => center.InternalId);
    this.availableDesks = this.desks.filter(desk => centerIds.indexOf(desk.CenterInternalId) !== -1);
  }

  centersChanged(selection: Map<string, DualListBoxItem>) {
    this.selectedCenters = Array.from(selection.values()).map(item => item.Data as CenterLite);
    this.updateAvailableDesks();
    const availableIds = this.availableDesks.map(desk => desk.InternalId);
    this.selectedDesks = this.selectedDesks.filter(desk => availableIds.indexOf(desk.InternalId) !== -1);
  }

  desksChanged(selection: Map<string, DualListBoxItem>) {
    this.selectedDesks = Array.from(selection.values()).map(item => item.Data as Desk);
  }

  addRecipient() {
    const email = this.recipientInput.trim().toLowerCase();
    if (!email) { return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.errorKey = 'turns-periodic.invalid email';
      return;
    }
    if (this.recipients.indexOf(email) === -1) { this.recipients.push(email); }
    this.recipientInput = '';
    this.errorKey = '';
  }

  removeRecipient(email: string) {
    this.recipients = this.recipients.filter(item => item !== email);
  }

  save() {
    if (this.saving || !this.ready) { return; }
    if (this.recipientInput.trim()) {
      this.addRecipient();
      if (this.errorKey) { return; }
    }
    this.errorKey = this.validate();
    if (this.errorKey) { return; }
    const request: PeriodicReportConfigRequest = {
      name: this.name.trim(),
      periodicity: this.periodicity,
      timeZone: this.timeZone.trim(),
      lang: this.lang,
      active: this.active,
      thresholdConfig: JSON.stringify(this.thresholds),
      recipients: this.recipients.slice(),
      centersInternalIds: this.selectedCenters.map(center => center.InternalId),
      turnDesksInternalIds: this.selectedDesks.map(desk => desk.InternalId)
    };
    if (this.internalId) { request.internalId = this.internalId; }
    this.saving = true;
    this.service.save(request).subscribe(
      () => {
        this.saving = false;
        this.snack.open(this.translate.instant('turns-periodic.saved'), 'success');
        this.router.navigate(['/turns/reports/periodic/configs']);
      },
      error => { this.saving = false; this.showError(error); }
    );
  }

  private validate(): string {
    if (!this.name || !this.name.trim() || !this.periodicity || !this.timeZone || !this.timeZone.trim() || !this.lang) {
      return 'turns-periodic.required fields';
    }
    if (this.periodicity !== 'WEEKLY' && this.periodicity !== 'MONTHLY') {
      return 'turns-periodic.required fields';
    }
    try { new Intl.DateTimeFormat('en', { timeZone: this.timeZone.trim() }); }
    catch (error) { return 'turns-periodic.invalid time zone'; }
    const wait = Number(this.thresholds.waitThresholdSeconds);
    const good = Number(this.thresholds.goodPercent);
    const warning = Number(this.thresholds.warningPercent);
    if (!Number.isInteger(wait) || wait <= 0 || !Number.isFinite(good) || !Number.isFinite(warning)
        || good < 0 || good > 100 || warning < 0 || warning > good) {
      return 'turns-periodic.invalid thresholds';
    }
    this.thresholds = { waitThresholdSeconds: wait, goodPercent: good, warningPercent: warning };
    return '';
  }

  private showError(error: any) {
    this.snack.open(error.error && error.error.message || error.message, 'error');
  }
}
